import type { ReactNode } from "react";
import {
  CHART_PAD,
  axisMoney,
  formatFall,
  stepPath,
  timeTicks,
  type ChartFrame,
  type DatedValue,
} from "../portfolio-tracker";

/** The money gridlines and their labels, with zero drawn darker when the chart goes below it. */
export function Gridlines({ frame }: { frame: ChartFrame }) {
  return (
    <>
      {frame.gridlines.map((line) => (
        <g key={line.value}>
          <line
            className={line.zero ? "tracker-chart-zero" : "tracker-chart-grid"}
            x1={frame.left}
            x2={frame.right}
            y1={line.y}
            y2={line.y}
          />
          <text className="tracker-chart-tick" x={frame.left - 8} y={line.y + 3.5} textAnchor="end">
            {axisMoney(line.value)}
          </text>
        </g>
      ))}
    </>
  );
}

// Room for "Jan 2026" in the chart's mono ticks, so a phone-width chart never runs its labels together.
const TICK_ROOM = 64;

/** Month or week labels along the bottom, leaving the ends clear. */
export function TimeTicks({ frame, from, to }: { frame: ChartFrame; from: string; to: string }) {
  const most = Math.max(2, Math.min(8, Math.floor((frame.right - frame.left) / TICK_ROOM)));
  return (
    <>
      {timeTicks(from, to, most)
        .filter(({ day }) => frame.x(day) > frame.left + 16 && frame.x(day) < frame.right - 16)
        .map(({ day, label }) => (
          <text key={day} className="tracker-chart-tick" x={frame.x(day)} y={frame.height - 8} textAnchor="middle">
            {label}
          </text>
        ))}
    </>
  );
}

/** The worst fall from a high, shaded across the plot with its size on top. */
export function DrawdownZone({
  frame,
  from,
  to,
  percent,
}: {
  frame: ChartFrame;
  from: string | null;
  to: string | null;
  percent: number;
}) {
  if (!from || !to || percent < 0.05) return null;
  const x1 = frame.x(from);
  const x2 = Math.max(x1 + 2, frame.x(to));
  return (
    <>
      <rect
        className="tracker-chart-dd-zone"
        x={x1}
        y={CHART_PAD.top}
        width={x2 - x1}
        height={frame.height - CHART_PAD.top - CHART_PAD.bottom}
      />
      <text className="tracker-chart-dd-label" x={(x1 + x2) / 2} y={CHART_PAD.top + 11} textAnchor="middle">
        {formatFall(percent)}
      </text>
    </>
  );
}

/** The last point, ringed, with its value beside it. */
export function EndMark({ x, y, label }: { x: number; y: number; label: string }) {
  const labelY = y - 10 > CHART_PAD.top + 8 ? y - 10 : y + 18;
  return (
    <>
      <circle className="tracker-chart-end" cx={x} cy={y} r={4.5} />
      <text className="tracker-chart-label" x={x - 6} y={labelY} textAnchor="end">
        {label}
      </text>
    </>
  );
}

/** Where the pointer is: a line down the plot and a dot on the series. */
export function HoverMarks({ frame, x, y }: { frame: ChartFrame; x: number; y: number }) {
  return (
    <>
      <line
        className="tracker-chart-cross"
        x1={x}
        x2={x}
        y1={CHART_PAD.top}
        y2={frame.height - CHART_PAD.bottom}
      />
      <circle className="tracker-chart-dot" cx={x} cy={y} r={4.5} />
    </>
  );
}

/** The value and day under the pointer, on whichever side of it has room. */
export function ChartTip({ frame, x, children }: { frame: ChartFrame; x: number; children: ReactNode }) {
  const style = x > frame.width / 2 ? { right: frame.width - x + 12 } : { left: x + 12 };
  return (
    <div className="tracker-chart-tip" style={style}>
      {children}
    </div>
  );
}

/** A stepped line from the first point to today, shaded beneath, with a dashed line at `base`. */
export function SteppedLine({
  frame,
  points,
  base,
  endLabel,
  zone,
  hoverDay,
  hoverValue,
}: {
  frame: ChartFrame;
  points: DatedValue[];
  base: number;
  endLabel: string;
  zone: ReactNode;
  hoverDay: string | null;
  hoverValue: number;
}) {
  const plotted = points.map((point) => ({ x: frame.x(point.date), y: frame.y(point.value) }));
  const line = stepPath(plotted);
  const area = `${line}V${frame.height - CHART_PAD.bottom}H${plotted[0].x.toFixed(1)}Z`;
  const last = plotted[plotted.length - 1];

  return (
    <>
      <Gridlines frame={frame} />
      <path className="tracker-chart-area" d={area} />
      {zone}
      <line className="tracker-chart-base" x1={frame.left} x2={frame.right} y1={frame.y(base)} y2={frame.y(base)} />
      <path className="tracker-chart-line" d={line} />
      <TimeTicks frame={frame} from={points[0].date} to={points[points.length - 1].date} />
      <EndMark x={last.x} y={last.y} label={endLabel} />
      {hoverDay && <HoverMarks frame={frame} x={frame.x(hoverDay)} y={frame.y(hoverValue)} />}
    </>
  );
}
